import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { FileView } from '@components/block/FileView/FileView';
import { AlertDialog } from '@components/ui';
import {
  CustomTraining,
  useArchiveCustomTrainingMutation,
  useCustomTrainingsQuery,
} from '@dieterApi/customTraining/useCustomTrainings';
import AddIcon from '@mui/icons-material/Add';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Skeleton, Tooltip } from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CustomTrainingModal, renewalLabel } from './CustomTrainingModal';

interface Props {
  open: boolean;
  onClose: () => void;
}

// Verwaltung der eigenen Schulungen als Modal (bewusst nicht im Tab selbst: Randfunktion,
// soll bei leerer Mitarbeiterliste nicht wie der erste Schritt wirken)
export function CustomTrainingsModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const { user, activeCompany } = useUserWithCompany();
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading } = useCustomTrainingsQuery(!open);
  const [archiveTraining] = useArchiveCustomTrainingMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editTraining, setEditTraining] = useState<CustomTraining | undefined>();
  const [previewTraining, setPreviewTraining] = useState<CustomTraining | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<CustomTraining | null>(null);

  const trainings = data?.customTrainings ?? [];
  const activeEmployeeIds = new Set((activeCompany?.employees ?? []).filter((e) => !e.archivedAt).map((e) => e.id));
  const readOnly = !!user?.isReadOnly;

  const handleArchive = () => {
    if (!archiveTarget) return;
    archiveTraining({ variables: { id: archiveTarget.id } })
      .then(() =>
        enqueueSnackbar(t('dashboard.training.custom.snackbars.archived', 'Schulung archiviert'), {
          variant: 'success',
        })
      )
      .catch((e) => enqueueSnackbar(e.message, { variant: 'error' }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 2 } }}
      data-testid="custom-trainings"
    >
      <DialogTitle sx={{ pt: 3, pb: 1, px: 3, pr: 7 }}>
        {t('dashboard.training.custom.title', 'Eigene Schulungen')}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 16, top: 20 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div className="flex flex-wrap items-center gap-2">
          <p className="grow text-sm text-gray-500">
            {t(
              'dashboard.training.custom.subtitle',
              'Eigene Dokumente zur Kenntnisnahme, z. B. interne Richtlinien oder Arbeitsanweisungen.'
            )}
          </p>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            disabled={readOnly}
            onClick={() => {
              setEditTraining(undefined);
              setModalOpen(true);
            }}
            data-testid="add-custom-training-button"
          >
            {t('dashboard.training.custom.add', 'Eigene Schulung anlegen')}
          </Button>
        </div>

        {loading && !data ? (
          <Skeleton variant="rounded" height={56} />
        ) : trainings.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 px-4 py-5 text-sm text-gray-500 text-center">
            {t('dashboard.training.custom.empty', 'Noch keine eigenen Schulungen angelegt.')}
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-gray-200 rounded-lg border border-gray-200">
            {trainings.map((training) => {
              const confirmed = training.confirmations.filter(
                (c) => !c.isStale && activeEmployeeIds.has(c.employeeId)
              ).length;
              return (
                <div
                  key={training.id}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3"
                  data-testid="custom-training-row"
                >
                  <div className="min-w-0 grow basis-60">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{training.title}</span>
                      <Chip
                        size="small"
                        label={t('dashboard.training.custom.types.Acknowledgement', 'Dokument zur Kenntnisnahme')}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {training.file?.name} · {renewalLabel(t, training.renewalMonths)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 whitespace-nowrap">
                    {t('dashboard.training.custom.confirmed_count', '{{confirmed}} / {{total}} bestätigt', {
                      confirmed,
                      total: activeEmployeeIds.size,
                    })}
                  </div>
                  <div className="flex gap-1">
                    <Tooltip title={t('dashboard.training.custom.actions.preview', 'Dokument ansehen')}>
                      <IconButton size="small" onClick={() => setPreviewTraining(training)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('dashboard.training.custom.actions.edit', 'Bearbeiten')}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={readOnly}
                          onClick={() => {
                            setEditTraining(training);
                            setModalOpen(true);
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={t(
                        'dashboard.training.custom.actions.archive',
                        'Schulung archivieren (Bestätigungen bleiben erhalten)'
                      )}
                    >
                      <span>
                        <IconButton size="small" disabled={readOnly} onClick={() => setArchiveTarget(training)}>
                          <ArchiveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <CustomTrainingModal open={modalOpen} training={editTraining} onClose={() => setModalOpen(false)} />

        <Dialog open={!!previewTraining} onClose={() => setPreviewTraining(null)} fullWidth maxWidth="md">
          <DialogTitle>{previewTraining?.title}</DialogTitle>
          <DialogContent>{previewTraining && <FileView fileId={previewTraining.fileId} />}</DialogContent>
        </Dialog>

        <AlertDialog
          open={!!archiveTarget}
          setOpen={(open: boolean) => !open && setArchiveTarget(null)}
          title={t('dashboard.training.custom.dialogs.archive.title', 'Schulung archivieren')}
          message={
            <Trans
              t={t}
              i18nKey="dashboard.training.custom.dialogs.archive.message"
              components={{ bold: <b /> }}
              shouldUnescape
              values={{ title: archiveTarget?.title }}
            />
          }
          acceptText={t('dashboard.training.custom.dialogs.archive.acceptText', 'Archivieren')}
          onAccept={handleArchive}
        />
      </DialogContent>
    </Dialog>
  );
}
